import React, { useState, useCallback, useRef } from 'react';
import {
  Box,
  Button,
  Text,
  VStack,
  HStack,
  Image,
  Grid,
  GridItem,
  IconButton,
  Badge,
  Progress,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  AspectRatio,
  Alert,
  AlertIcon,
  AlertDescription,
} from '@chakra-ui/react';
import { FaUpload, FaImage, FaTimes, FaPlus, FaEye } from 'react-icons/fa';

interface ImageFile {
  id: string;
  file: File;
  preview: string;
  name: string;
  size: number;
  isUploading?: boolean;
  uploadProgress?: number;
  error?: string;
}

interface ImageUploaderProps {
  maxFiles?: number;
  maxFileSize?: number; // in MB
  acceptedTypes?: string[];
  onImagesChange: (images: ImageFile[]) => void;
  images: ImageFile[];
  required?: boolean;
  helperText?: string;
  errorMessage?: string;
}

export const ImageUploader: React.FC<ImageUploaderProps> = ({
  maxFiles = 6,
  maxFileSize = 10, // 10MB
  acceptedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'],
  onImagesChange,
  images,
  required = false,
  helperText,
  errorMessage,
}) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const [previewImage, setPreviewImage] = useState<ImageFile | null>(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const toast = useToast();

  const generateImageId = () => `img_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

  const validateFile = (file: File): string | null => {
    if (!acceptedTypes.includes(file.type)) {
      return `File type ${file.type} is not supported. Please use JPG, PNG, or WebP.`;
    }
    if (file.size > maxFileSize * 1024 * 1024) {
      return `File size exceeds ${maxFileSize}MB limit.`;
    }
    return null;
  };

  const processFiles = useCallback((fileList: FileList) => {
    const files = Array.from(fileList);
    const currentImageCount = images.length;
    
    if (currentImageCount + files.length > maxFiles) {
      toast({
        title: 'Too Many Files',
        description: `You can only upload up to ${maxFiles} images. Current: ${currentImageCount}, Trying to add: ${files.length}`,
        status: 'warning',
        duration: 4000,
        isClosable: true,
      });
      return;
    }

    const newImages: ImageFile[] = [];
    const errors: string[] = [];

    files.forEach((file) => {
      const error = validateFile(file);
      if (error) {
        errors.push(`${file.name}: ${error}`);
        return;
      }

      const imageFile: ImageFile = {
        id: generateImageId(),
        file,
        preview: URL.createObjectURL(file),
        name: file.name,
        size: file.size,
        isUploading: false,
        uploadProgress: 0,
      };

      newImages.push(imageFile);
    });

    if (errors.length > 0) {
      toast({
        title: 'File Validation Errors',
        description: errors.join('\n'),
        status: 'error',
        duration: 6000,
        isClosable: true,
      });
    }

    if (newImages.length > 0) {
      onImagesChange([...images, ...newImages]);
      toast({
        title: 'Images Added',
        description: `Successfully added ${newImages.length} image(s)`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    }
  }, [images, maxFiles, maxFileSize, acceptedTypes, onImagesChange, toast]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      processFiles(files);
    }
  }, [processFiles]);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      processFiles(files);
    }
    // Reset input value to allow selecting the same file again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [processFiles]);

  const removeImage = useCallback((imageId: string) => {
    const imageToRemove = images.find(img => img.id === imageId);
    if (imageToRemove) {
      URL.revokeObjectURL(imageToRemove.preview);
    }
    onImagesChange(images.filter(img => img.id !== imageId));
  }, [images, onImagesChange]);

  const reorderImages = useCallback((fromIndex: number, toIndex: number) => {
    const newImages = [...images];
    const [movedImage] = newImages.splice(fromIndex, 1);
    newImages.splice(toIndex, 0, movedImage);
    onImagesChange(newImages);
  }, [images, onImagesChange]);

  const makeMainImage = useCallback((imageId: string) => {
    const imageIndex = images.findIndex(img => img.id === imageId);
    if (imageIndex > 0) {
      reorderImages(imageIndex, 0);
      toast({
        title: 'Main Image Updated',
        description: 'This image is now the main photo for your listing',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    }
  }, [images, reorderImages, toast]);

  const openPreview = useCallback((image: ImageFile) => {
    setPreviewImage(image);
    setIsPreviewOpen(true);
  }, []);

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <Box>
      {/* Upload Area */}
      <Box
        border="2px dashed"
        borderColor={isDragOver ? 'brand.green.medium' : (errorMessage ? 'red.400' : 'brand.gray.300')}
        borderRadius="lg"
        p={8}
        textAlign="center"
        bg={isDragOver ? 'brand.green.50' : 'white'}
        transition="all 0.2s"
        cursor="pointer"
        _hover={{
          borderColor: 'brand.green.medium',
          bg: 'brand.green.50',
        }}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={acceptedTypes.join(',')}
          onChange={handleFileSelect}
          style={{ display: 'none' }}
        />
        
        <VStack spacing={4}>
          <Box>
            <FaUpload size={32} color={isDragOver ? 'var(--chakra-colors-brand-green-medium)' : 'var(--chakra-colors-brand-gray-400)'} />
          </Box>
          
          <VStack spacing={2}>
            <Text fontSize="lg" fontWeight="semibold" color="brand.green.dark">
              Drop images here or click to browse
            </Text>
            <Text fontSize="sm" color="brand.gray.500">
              Supports JPG, PNG, WebP up to {maxFileSize}MB each
            </Text>
            <Text fontSize="sm" color="brand.gray.500">
              Maximum {maxFiles} images {required && '(required)'}
            </Text>
          </VStack>
          
          <Button
            leftIcon={<FaPlus />}
            variant="outline"
            colorScheme="green"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              fileInputRef.current?.click();
            }}
          >
            Select Images
          </Button>
        </VStack>
      </Box>

      {/* Error Message */}
      {errorMessage && (
        <Alert status="error" mt={3} borderRadius="md">
          <AlertIcon />
          <AlertDescription>{errorMessage}</AlertDescription>
        </Alert>
      )}

      {/* Helper Text */}
      {helperText && !errorMessage && (
        <Text fontSize="sm" color="brand.gray.500" mt={2}>
          {helperText}
        </Text>
      )}

      {/* Image Grid */}
      {images.length > 0 && (
        <Box mt={6}>
          <HStack justify="space-between" align="center" mb={4}>
            <Text fontSize="lg" fontWeight="semibold" color="brand.green.dark">
              Images ({images.length}/{maxFiles})
            </Text>
            <Text fontSize="sm" color="brand.gray.500">
              First image will be the main photo
            </Text>
          </HStack>
          
          <Grid templateColumns="repeat(auto-fill, minmax(150px, 1fr))" gap={4}>
            {images.map((image, index) => (
              <GridItem key={image.id}>
                <Box
                  position="relative"
                  borderRadius="lg"
                  overflow="hidden"
                  bg="brand.gray.100"
                  border="2px solid"
                  borderColor={index === 0 ? 'brand.primary' : 'transparent'}
                  transition="all 0.2s"
                  _hover={{
                    borderColor: index === 0 ? 'brand.primaryHover' : 'brand.green.light',
                    transform: 'scale(1.02)',
                  }}
                >
                  {/* Main Image Badge */}
                  {index === 0 && (
                    <Badge
                      position="absolute"
                      top={2}
                      left={2}
                      bg="brand.primary"
                      color="black"
                      fontSize="xs"
                      fontWeight="bold"
                      px={2}
                      py={1}
                      borderRadius="md"
                      zIndex={2}
                    >
                      MAIN
                    </Badge>
                  )}
                  
                  {/* Image */}
                  <AspectRatio ratio={1}>
                    <Image
                      src={image.preview}
                      alt={image.name}
                      objectFit="cover"
                      w="full"
                      h="full"
                    />
                  </AspectRatio>
                  
                  {/* Upload Progress */}
                  {image.isUploading && (
                    <Box
                      position="absolute"
                      bottom={0}
                      left={0}
                      right={0}
                      bg="rgba(0,0,0,0.8)"
                      p={2}
                    >
                      <Progress
                        value={image.uploadProgress}
                        size="sm"
                        colorScheme="green"
                        bg="brand.gray.600"
                      />
                      <Text color="white" fontSize="xs" mt={1} textAlign="center">
                        Uploading... {image.uploadProgress}%
                      </Text>
                    </Box>
                  )}
                  
                  {/* Action Buttons */}
                  <Box
                    position="absolute"
                    top={2}
                    right={2}
                    opacity={0}
                    transition="opacity 0.2s"
                    _groupHover={{ opacity: 1 }}
                    className="image-actions"
                  >
                    <VStack spacing={1}>
                      <IconButton
                        aria-label="Preview image"
                        icon={<FaEye />}
                        size="sm"
                        variant="solid"
                        bg="rgba(0,0,0,0.7)"
                        color="white"
                        _hover={{ bg: 'rgba(0,0,0,0.9)' }}
                        onClick={(e) => {
                          e.stopPropagation();
                          openPreview(image);
                        }}
                      />
                      <IconButton
                        aria-label="Remove image"
                        icon={<FaTimes />}
                        size="sm"
                        variant="solid"
                        bg="rgba(220, 38, 38, 0.8)"
                        color="white"
                        _hover={{ bg: 'rgba(220, 38, 38, 1)' }}
                        onClick={(e) => {
                          e.stopPropagation();
                          removeImage(image.id);
                        }}
                      />
                    </VStack>
                  </Box>
                  
                  {/* Make Main Button */}
                  {index > 0 && (
                    <Box
                      position="absolute"
                      bottom={2}
                      left={2}
                      right={2}
                      opacity={0}
                      transition="opacity 0.2s"
                      _groupHover={{ opacity: 1 }}
                    >
                      <Button
                        size="xs"
                        variant="solid"
                        bg="rgba(0,0,0,0.8)"
                        color="white"
                        _hover={{ bg: 'rgba(0,0,0,1)' }}
                        onClick={(e) => {
                          e.stopPropagation();
                          makeMainImage(image.id);
                        }}
                        width="full"
                      >
                        Make Main
                      </Button>
                    </Box>
                  )}
                  
                  {/* File Info */}
                  <Box
                    position="absolute"
                    bottom={0}
                    left={0}
                    right={0}
                    bg="rgba(0,0,0,0.7)"
                    color="white"
                    p={2}
                    opacity={0}
                    transition="opacity 0.2s"
                    _groupHover={{ opacity: 1 }}
                  >
                    <Text fontSize="xs" noOfLines={1} fontWeight="medium">
                      {image.name}
                    </Text>
                    <Text fontSize="xs" opacity={0.8}>
                      {formatFileSize(image.size)}
                    </Text>
                  </Box>
                  
                  {/* Error State */}
                  {image.error && (
                    <Box
                      position="absolute"
                      top={0}
                      left={0}
                      right={0}
                      bottom={0}
                      bg="rgba(220, 38, 38, 0.9)"
                      color="white"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      p={2}
                    >
                      <Text fontSize="xs" textAlign="center">
                        {image.error}
                      </Text>
                    </Box>
                  )}
                  
                  {/* Hover Effect */}
                  <Box className="group" />
                </Box>
              </GridItem>
            ))}
          </Grid>
        </Box>
      )}

      {/* Image Preview Modal */}
      <Modal isOpen={isPreviewOpen} onClose={() => setIsPreviewOpen(false)} size="4xl">
        <ModalOverlay bg="blackAlpha.800" />
        <ModalContent bg="white" maxW="90vw" maxH="90vh">
          <ModalHeader>
            <HStack>
              <FaImage />
              <Text>{previewImage?.name}</Text>
            </HStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody p={0}>
            {previewImage && (
              <Box maxH="70vh" overflow="hidden">
                <Image
                  src={previewImage.preview}
                  alt={previewImage.name}
                  objectFit="contain"
                  w="full"
                  h="full"
                  maxH="70vh"
                />
              </Box>
            )}
          </ModalBody>
          <ModalFooter>
            <HStack spacing={4}>
              <Text fontSize="sm" color="brand.gray.500">
                {previewImage && formatFileSize(previewImage.size)}
              </Text>
              <Button onClick={() => setIsPreviewOpen(false)}>
                Close
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default ImageUploader; 